import { getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ComponentProps, ReactNode, useMemo } from 'react';

import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table';
import { AdminSession } from '@airreading/common/data';
import {
  futureSessionsPageColumns,
  overlappingSessionsPageColumns,
  pastSessionsPageColumns,
  sessionsPageColumns,
  subSessionsPageColumns,
  teacherFutureSessionsPageColumns,
  teacherPastSessionsPageColumns,
  todaySessionsPageColumns,
} from './sessions-page-columns';

export type EntityTableProps = ComponentProps<typeof EntityTable>;

export interface SessionsTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessions: AdminSession[];
  errorState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  filters?: EntityTableProps['filters'];
  tableHookProps?: Omit<TableOptions<AdminSession>, 'columns' | 'data' | 'getCoreRowModel'>;
  displayTimeZone: string;
  bulkActionsMenuItems?: ReactNode;
  topBarRightComponents?: ReactNode;
  paginationOptions?: number[];
  from?: 'future' | 'past' | 'today' | 'sub' | 'overlapping' | 'school' | 'teacher-future' | 'teacher-past';
  groupBy?: EntityTableGroupBy<AdminSession, GroupKey>;
}

export function SessionsTable<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessions,
  errorState,
  isLoading,
  hasError,
  filters,
  tableHookProps,
  displayTimeZone,
  bulkActionsMenuItems,
  topBarRightComponents,
  from,
  paginationOptions,
  groupBy,
}: SessionsTableProps<GroupKey>) {
  const columns = useMemo(() => {
    switch (from) {
      case 'future':
        return futureSessionsPageColumns(displayTimeZone);
      case 'today':
        return todaySessionsPageColumns(displayTimeZone);
      case 'sub':
        return subSessionsPageColumns(displayTimeZone);
      case 'overlapping':
        return overlappingSessionsPageColumns(displayTimeZone);
      case 'past':
        return pastSessionsPageColumns(displayTimeZone);
      case 'teacher-future':
        return teacherFutureSessionsPageColumns(displayTimeZone);
      case 'teacher-past':
        return teacherPastSessionsPageColumns(displayTimeZone);
      default:
        return sessionsPageColumns(displayTimeZone, from);
    }
  }, [displayTimeZone, from]);

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...tableHookProps,
    defaultColumn: {
      enableSorting: false,
      ...tableHookProps?.defaultColumn,
    },
    state: {
      ...tableHookProps?.state,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState={'No sessions found'}
      errorState={errorState}
      hasError={hasError}
      isLoading={isLoading}
      filters={filters}
      showPagination={true}
      bulkActionsMenuItems={bulkActionsMenuItems}
      topBarRightComponents={topBarRightComponents}
      paginationOptions={paginationOptions}
      headerClassName={from === 'today' ? 'px-2 py-0 m-0' : undefined}
      rowClassName={from === 'today' ? 'px-2 py-0 m-0' : undefined}
      emptyStateClassName="translate-y-0"
      groupBy={groupBy}
    />
  );
}
